import { useMutation, useQuery } from "@apollo/client";
import { Box, Stack, TextField } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { RadioGroupField } from "components/Forms/RadioGroupField";
import { gql } from "generated-graphql";
import {
  FacilityPilotProgramQuery,
  FacilityPilotProgramReadyStatus,
} from "generated-graphql/graphql";
import { useCallback } from "react";
import { useForm, Controller } from "react-hook-form";

const FACILITY_PILOT_PROGRAM_QUERY = gql(`
  query FacilityPilotProgram($state: String!, $reportingYear: Int!) {
    facilityPilotProgram(state: $state, reportingYear: $reportingYear) {
      id
      state
      reportingYear
      status
      notes
      createdAt
      createdBy
    }
  }
`);

const UPSERT_FACILITY_PILOT_PROGRAM_MUTATION = gql(`
  mutation UpsertFacilityPilotProgram($input: UpsertFacilityPilotProgramInput!) {
    upsertFacilityPilotProgram(input: $input) {
      id
      state
      reportingYear
      status
      notes
      createdAt
      createdBy
    }
  }
`);

interface EditPilotProgramModalProps {
  open: boolean;
  onClose: () => void;
  state: string;
  reportingYear: number;
  onSave: () => void;
}

type FormData = {
  status: FacilityPilotProgramReadyStatus;
  notes: string;
};

export function EditPilotProgramModal({
  open,
  onClose,
  state,
  reportingYear,
  onSave,
}: EditPilotProgramModalProps) {
  const alerts = useAlerts();
  const { control, handleSubmit, reset } = useForm<FormData>({
    defaultValues: {
      status: FacilityPilotProgramReadyStatus.NotReady,
      notes: "",
    },
  });

  const { data, loading: queryLoading } = useQuery<FacilityPilotProgramQuery>(
    FACILITY_PILOT_PROGRAM_QUERY,
    {
      variables: { state, reportingYear },
      skip: !open,
      fetchPolicy: "network-only",
      onCompleted: (data) => {
        if (data.facilityPilotProgram) {
          reset({
            status:
              data.facilityPilotProgram.status ??
              FacilityPilotProgramReadyStatus.NotReady,
            notes: data.facilityPilotProgram.notes ?? "",
          });
        } else {
          // No record exists, use defaults
          reset({
            status: FacilityPilotProgramReadyStatus.NotReady,
            notes: "",
          });
        }
      },
    }
  );

  const [upsertMutation, { loading: saveLoading }] = useMutation(
    UPSERT_FACILITY_PILOT_PROGRAM_MUTATION,
    {
      onCompleted: () => {
        alerts.success("Pilot program settings saved");
        onSave();
        onClose();
      },
      onError: (error) => {
        alerts.error(`Failed to save: ${error.message}`);
      },
    }
  );

  const onSubmit = useCallback(
    (formData: FormData) => {
      upsertMutation({
        variables: {
          input: {
            state,
            reportingYear,
            status: formData.status,
            notes: formData.notes || null,
          },
        },
      });
    },
    [upsertMutation, state, reportingYear]
  );

  const handleClose = useCallback(() => {
    if (!saveLoading) {
      reset();
      onClose();
    }
  }, [saveLoading, reset, onClose]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title={`Edit Pilot Program - ${state}`}
      maxWidth="sm"
      fullWidth
      primaryActionButtonProps={{
        loading: saveLoading,
        onClick: handleSubmit(onSubmit),
        disabled: queryLoading,
      }}
    >
      <Box sx={{ pt: 1, px: 3, pb: 3 }}>
        <Stack spacing={2}>
          <RadioGroupField
            name="status"
            control={control}
            label="Allow State"
            radioOptions={[
              {
                value: FacilityPilotProgramReadyStatus.Ready,
                label: "Ready",
              },
              {
                value: FacilityPilotProgramReadyStatus.NotReady,
                label: "Not Ready",
              },
            ]}
          />

          <Controller
            name="notes"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Notes"
                multiline
                rows={4}
                fullWidth
                variant="outlined"
                placeholder="Add notes about this pilot program..."
              />
            )}
          />
        </Stack>
      </Box>
    </Dialog>
  );
}

